import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import gsap from "gsap";
import {
  CLAY_HEIGHT,
  CLAY_RADIUS,
  DISPLACEMENT_STRENGTH,
  HEIGHT_DESKTOP,
  HEIGHT_MOBILE,
  RADIAL_DESKTOP,
  RADIAL_MOBILE,
} from "./constants";
import { createClayTextures } from "./clayTextures";

interface ClayProps {
  displacementMap: THREE.Texture;
  color: string;
  isMobile: boolean;
  reducedMotion: boolean;
  /** [0,1] normalized wheel speed. */
  speedRef: React.MutableRefObject<number>;
  /** Reports current base color for tinted sheen. */
  onReady?: (mesh: THREE.Mesh) => void;
}

export interface ClayHandle {
  mesh: THREE.Mesh | null;
  material: THREE.MeshPhysicalMaterial | null;
  /** Trigger the "flash" wobble when swatch changes. */
  flash: () => void;
}

/**
 * The clay mesh. Vertex shader injects two effects on top of MeshPhysicalMaterial:
 *   1. Persistent displacement from the paintable texture (offset along normal).
 *   2. Centrifugal bulge (radial outward, increases with height and wheel speed).
 */
export const Clay = forwardRef<ClayHandle, ClayProps>(function Clay(
  { displacementMap, color, isMobile, reducedMotion, speedRef, onReady },
  ref,
) {
  const meshRef = useRef<THREE.Mesh>(null);
  const materialRef = useRef<THREE.MeshPhysicalMaterial>(null);

  // Procedural clay-grain normal + roughness maps.
  const clayTextures = useMemo(() => createClayTextures(isMobile), [isMobile]);
  useEffect(() => {
    return () => {
      clayTextures.dispose();
    };
  }, [clayTextures]);

  // Custom uniforms for our shader injection.
  const uniforms = useMemo(
    () => ({
      uDispMap: { value: displacementMap },
      uDispStrength: { value: DISPLACEMENT_STRENGTH },
      uSpeed: { value: 0.0 },
      uCentrifugal: { value: reducedMotion ? 0.0 : 0.35 },
      uFlash: { value: 0.0 },
      uTime: { value: 0.0 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displacementMap],
  );

  const geometry = useMemo(() => {
    const radial = isMobile ? RADIAL_MOBILE : RADIAL_DESKTOP;
    const heightSeg = isMobile ? HEIGHT_MOBILE : HEIGHT_DESKTOP;
    // Slightly barrel-y starting shape: use LatheGeometry so we can seed a lump.
    const points: THREE.Vector2[] = [];
    for (let i = 0; i <= heightSeg; i++) {
      const t = i / heightSeg;
      // Gentle centered lump around v=0.5
      const bulge = Math.sin(t * Math.PI) * 0.25;
      const r = CLAY_RADIUS * (0.78 + bulge * 0.6);
      const y = t * CLAY_HEIGHT;
      points.push(new THREE.Vector2(r, y));
    }
    const g = new THREE.LatheGeometry(points, radial);
    g.computeVertexNormals();
    // Duplicate uv → uv2 for the AO map (three.js requires uv2 for aoMap sampling).
    const uv = g.attributes.uv;
    if (uv) {
      g.setAttribute("uv2", new THREE.BufferAttribute(new Float32Array(uv.array), 2));
    }
    return g;
  }, [isMobile]);

  // Hook into shader via onBeforeCompile.
  const onBeforeCompile = useMemo(() => {
    return (shader: THREE.WebGLProgramParametersWithUniforms) => {
      shader.uniforms.uDispMap = uniforms.uDispMap;
      shader.uniforms.uDispStrength = uniforms.uDispStrength;
      shader.uniforms.uSpeed = uniforms.uSpeed;
      shader.uniforms.uCentrifugal = uniforms.uCentrifugal;
      shader.uniforms.uFlash = uniforms.uFlash;
      shader.uniforms.uTime = uniforms.uTime;
      shader.uniforms.uClayHeight = { value: CLAY_HEIGHT };
      shader.uniforms.uClayRadius = { value: CLAY_RADIUS };

      shader.vertexShader = shader.vertexShader
        .replace(
          "#include <common>",
          `
          #include <common>
          uniform sampler2D uDispMap;
          uniform float uDispStrength;
          uniform float uSpeed;
          uniform float uCentrifugal;
          uniform float uTime;
          uniform float uClayHeight;
          uniform float uClayRadius;
          `,
        )
        .replace(
          "#include <begin_vertex>",
          `
          vec3 transformed = vec3(position);

          // 1) Sample displacement canvas (bilinear via linear filter).
          float disp = texture2D(uDispMap, uv).r - 0.5;

          // Push along vertex normal.
          transformed += normal * disp * uDispStrength;

          // 2) Centrifugal bulge: radial outward, increases with height^2 and speed.
          float vNorm = clamp(position.y / uClayHeight, 0.0, 1.0);
          vec3 radialAxis = vec3(position.x, 0.0, position.z);
          float radialLen = length(radialAxis) + 0.0001;
          vec3 radialDir = radialAxis / radialLen;
          float bulge = pow(vNorm, 2.0) * uSpeed * uCentrifugal;
          transformed += radialDir * bulge * uClayRadius * 0.35;

          // Tiny living wobble at high speeds (very subtle).
          float wobble = sin(uTime * 6.0 + position.y * 2.0) * 0.0025 * uSpeed;
          transformed += radialDir * wobble;
          `,
        );

      // Add a warm flash tint in fragment.
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `
          #include <common>
          uniform float uFlash;
          `,
        )
        .replace(
          "#include <dithering_fragment>",
          `
          #include <dithering_fragment>
          // Warm copper flash pulse when color changes.
          gl_FragColor.rgb += vec3(0.83, 0.65, 0.45) * uFlash * 0.35;
          `,
        );
    };
  }, [uniforms]);

  useFrame((_state, deltaRaw) => {
    const delta = Math.min(deltaRaw, 0.1);
    uniforms.uTime.value += delta;
    // Lerp speed uniform to current speed ref for smoothness.
    uniforms.uSpeed.value = THREE.MathUtils.lerp(uniforms.uSpeed.value, speedRef.current, 0.08);
    // Rotate.
    const rotSpeed = reducedMotion ? 0 : speedRef.current * 5;
    if (meshRef.current) {
      meshRef.current.rotation.y += rotSpeed * delta;
    }
  });

  useEffect(() => {
    if (meshRef.current && onReady) onReady(meshRef.current);
  }, [onReady]);

  // Animate material color smoothly when the color prop changes.
  const prevColorRef = useRef<string | null>(null);
  useEffect(() => {
    const mat = materialRef.current;
    if (!mat) return;
    // On mount, snap to initial color (skip animation).
    if (prevColorRef.current === null) {
      prevColorRef.current = color;
      mat.color.set(color);
      return;
    }
    const from = new THREE.Color(prevColorRef.current);
    const target = new THREE.Color(color);
    prevColorRef.current = color;
    const proxy = { r: from.r, g: from.g, b: from.b };
    mat.color.copy(from);
    gsap.to(proxy, {
      r: target.r,
      g: target.g,
      b: target.b,
      duration: reducedMotion ? 0.001 : 0.8,
      ease: "power2.inOut",
      onUpdate: () => {
        mat.color.setRGB(proxy.r, proxy.g, proxy.b);
      },
    });
  }, [color, reducedMotion]);

  useImperativeHandle(
    ref,
    () => ({
      mesh: meshRef.current,
      material: materialRef.current,
      flash: () => {
        uniforms.uFlash.value = 0.2;
        // simple lerp back to 0 via a timeout-driven decay
        const start = performance.now();
        const decay = () => {
          const t = (performance.now() - start) / 200;
          uniforms.uFlash.value = Math.max(0, 0.2 * (1 - t));
          if (t < 1) requestAnimationFrame(decay);
          else uniforms.uFlash.value = 0;
        };
        requestAnimationFrame(decay);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniforms],
  );

  return (
    <mesh ref={meshRef} geometry={geometry} castShadow receiveShadow position={[0, 0, 0]}>
      <meshPhysicalMaterial
        ref={materialRef}
        roughness={0.95}
        metalness={0.0}
        clearcoat={0.0}
        sheen={0.0}
        reflectivity={0.15}
        specularIntensity={0.25}
        normalMap={clayTextures.normalMap}
        normalScale={new THREE.Vector2(1.1, 1.1)}
        roughnessMap={clayTextures.roughnessMap}
        aoMap={clayTextures.aoMap}
        aoMapIntensity={1.2}
        flatShading={false}
        side={THREE.DoubleSide}
        onBeforeCompile={onBeforeCompile}
      />
    </mesh>
  );
});